/**
 * Fase 5 Test Component
 * 
 * Test component for Activity Cache & Persistence
 * Demonstrates ActivityCacheManager with localStorage persistence and query API
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 5 Implementation
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Save,
  Database,
  Clock,
  TrendingUp,
  Filter,
  Search,
  HardDrive,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';
import { ActivityCacheManager, TimeWindow, CacheStats, QueryResult } from './ActivityCacheManager';
import * as localStorageHelper from '../../../../utils/localStorageHelper';

/**
 * Generate mock activity event for testing
 */
const generateMockEvent = (): LocationActivityEvent => {
  const racks = [1, 2, 3, 4, 5];
  const verticals = [1, 2, 3, 4, 5];
  const shelves = [0, 1, 2, 3, 4, 5];
  
  const rackNumber = racks[Math.floor(Math.random() * racks.length)];
  const vertical = verticals[Math.floor(Math.random() * verticals.length)];
  const shelf = shelves[Math.floor(Math.random() * shelves.length)];
  
  const binCode = `R${rackNumber.toString().padStart(2, '0')}-V${vertical.toString().padStart(2, '0')}-H${shelf.toString().padStart(2, '0')}`;
  
  const activityTypes = [
    ActivityType.Pick,
    ActivityType.PutAway,
    ActivityType.Transfer,
    ActivityType.CorrectionOut,
    ActivityType.CorrectionIn,
    ActivityType.Counting,
    ActivityType.Receiving,
    ActivityType.Reservation
  ];
  
  return {
    binCode,
    rackNumber,
    activityType: activityTypes[Math.floor(Math.random() * activityTypes.length)],
    timestamp: new Date().toISOString(),
    operatorId: `OP${Math.floor(Math.random() * 10) + 1}`,
    itemNo: `ITEM${Math.floor(Math.random() * 1000)}`,
    quantity: Math.floor(Math.random() * 50) + 1,
    companyNo: '1',
    customerNo: 'TEST',
    warehouseId: 'WH001'
  };
};

export const Fase5Test: React.FC = () => {
  const cacheManagerRef = useRef<ActivityCacheManager | null>(null);
  
  const [stats, setStats] = useState<CacheStats>({
    totalEvents: 0,
    maxSize: 10000,
    usagePercentage: 0,
    totalEventsAdded: 0,
    totalEventsEvicted: 0,
    totalEventsDropped: 0,
    restoredFromStorageCount: 0
  });
  
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [generationSpeed, setGenerationSpeed] = useState<number>(1000); // milliseconds
  const [storageStats, setStorageStats] = useState<ReturnType<typeof localStorageHelper.getStorageStats>>(null);
  
  // Query filters
  const [queryType, setQueryType] = useState<'location' | 'rack' | 'timeWindow'>('location');
  const [queryLocation, setQueryLocation] = useState<string>('R01-V01-H00');
  const [queryRack, setQueryRack] = useState<number>(1);
  const [queryTimeWindow, setQueryTimeWindow] = useState<TimeWindow>(TimeWindow.Last1Hour);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  
  const [eventLog, setEventLog] = useState<LocationActivityEvent[]>([]);

  /**
   * Initialize cache manager on mount
   */
  useEffect(() => {
    console.log('[Fase5Test] Initializing ActivityCacheManager');
    
    cacheManagerRef.current = new ActivityCacheManager({
      maxCacheSize: 10000,
      enablePersistence: true,
      persistenceDebounceMs: 2000,
      enableMidnightReset: true,
      enableStats: true
    });

    // Update stats initially
    updateStats();
    updateStorageStats();

    // Cleanup on unmount
    return () => {
      if (cacheManagerRef.current) {
        cacheManagerRef.current.cleanup();
      }
    };
  }, []);

  /**
   * Update cache statistics
   */
  const updateStats = useCallback(() => {
    if (cacheManagerRef.current) {
      setStats(cacheManagerRef.current.getStats());
    }
  }, []);

  /**
   * Update localStorage statistics
   */
  const updateStorageStats = useCallback(() => {
    setStorageStats(localStorageHelper.getStorageStats());
  }, []);

  /**
   * Add single random event
   */
  const addRandomEvent = useCallback(() => {
    if (!cacheManagerRef.current) return;

    const event = generateMockEvent();
    cacheManagerRef.current.addEvent(event);
    
    // Update event log (keep last 20)
    setEventLog(prev => [event, ...prev].slice(0, 20));
    
    updateStats();
    updateStorageStats();
  }, [updateStats, updateStorageStats]);

  /**
   * Add multiple events
   */
  const addMultipleEvents = useCallback((count: number) => {
    if (!cacheManagerRef.current) return;

    const events: LocationActivityEvent[] = [];
    for (let i = 0; i < count; i++) {
      events.push(generateMockEvent());
    }

    const addedCount = cacheManagerRef.current.addEvents(events);
    console.log(`[Fase5Test] Added ${addedCount} events to cache`);
    
    updateStats();
    updateStorageStats();
  }, [updateStats, updateStorageStats]);

  /**
   * Clear cache
   */
  const clearCache = useCallback((clearStorage: boolean = true) => {
    if (!cacheManagerRef.current) return;

    cacheManagerRef.current.clear(clearStorage);
    setEventLog([]);
    setQueryResult(null);
    
    updateStats();
    updateStorageStats();
  }, [updateStats, updateStorageStats]);

  /**
   * Force save to localStorage
   */
  const forceSave = useCallback(() => {
    if (!cacheManagerRef.current) return;

    cacheManagerRef.current.forceSave();
    updateStorageStats();
    alert('Cache manuelt lagret til localStorage');
  }, [updateStorageStats]);

  /**
   * Execute query
   */
  const executeQuery = useCallback(() => {
    if (!cacheManagerRef.current) return;

    let result: QueryResult;

    switch (queryType) {
      case 'location':
        result = cacheManagerRef.current.queryByLocation(queryLocation, queryTimeWindow);
        break;
      case 'rack':
        result = cacheManagerRef.current.queryByRack(queryRack, queryTimeWindow);
        break;
      case 'timeWindow':
        result = cacheManagerRef.current.queryByTimeWindow(queryTimeWindow);
        break;
      default:
        return;
    }

    setQueryResult(result);
    console.log('[Fase5Test] Query result:', result);
  }, [queryType, queryLocation, queryRack, queryTimeWindow]);

  /**
   * Auto-generate events
   */
  useEffect(() => {
    if (!isAutoGenerating) return;

    const interval = setInterval(() => {
      addRandomEvent();
    }, generationSpeed);

    return () => clearInterval(interval);
  }, [isAutoGenerating, generationSpeed, addRandomEvent]);

  /**
   * Get activity type name
   */
  const getActivityTypeName = (type: ActivityType): string => {
    const names: Record<ActivityType, string> = {
      [ActivityType.Pick]: 'Plukk',
      [ActivityType.PutAway]: 'Plassering',
      [ActivityType.Transfer]: 'Overføring',
      [ActivityType.CorrectionOut]: 'Korr. Ut',
      [ActivityType.CorrectionIn]: 'Korr. Inn',
      [ActivityType.Counting]: 'Telling',
      [ActivityType.Receiving]: 'Mottak',
      [ActivityType.Reservation]: 'Reservasjon'
    };
    return names[type] || 'Ukjent';
  };

  return (
    <div className="h-screen flex flex-col p-6 bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-500" />
              Fase 5: Activity Cache & Persistence Test
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Test av cache med localStorage persistence, query API og midnight reset
            </p>
          </div>
        </div>
      </div>

      {/* Main content - 3 columns */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Column 1: Controls & Statistics */}
        <div className="w-96 flex flex-col gap-4 flex-shrink-0">
          {/* Cache Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Play className="w-5 h-5" />
              Cache kontroller
            </h3>
            
            <div className="space-y-2">
              <button
                onClick={addRandomEvent}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                Legg til 1 event
              </button>

              <button
                onClick={() => addMultipleEvents(100)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                Legg til 100 events
              </button>

              <button
                onClick={() => addMultipleEvents(1000)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                Legg til 1000 events
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsAutoGenerating(!isAutoGenerating)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isAutoGenerating
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                  }`}
                >
                  {isAutoGenerating ? (
                    <>
                      <Pause className="w-4 h-4" />
                      Stopp auto
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Start auto
                    </>
                  )}
                </button>

                <select
                  value={generationSpeed}
                  onChange={(e) => setGenerationSpeed(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={isAutoGenerating}
                >
                  <option value={500}>0.5s</option>
                  <option value={1000}>1s</option>
                  <option value={2000}>2s</option>
                  <option value={5000}>5s</option>
                </select>
              </div>

              <button
                onClick={forceSave}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                Lagre til localStorage nå
              </button>

              <button
                onClick={() => clearCache(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Tøm cache + localStorage
              </button>
            </div>
          </div>

          {/* Cache Statistics */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Cache statistikk
            </h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Events i cache:</span>
                <span className="font-semibold">{stats.totalEvents} / {stats.maxSize}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Bruk:</span>
                <span className="font-semibold">{stats.usagePercentage.toFixed(1)}%</span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(stats.usagePercentage, 100)}%` }}
                />
              </div>

              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Totalt lagt til:</span>
                  <span className="font-semibold">{stats.totalEventsAdded}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Totalt evicted:</span>
                  <span className="font-semibold text-orange-600">{stats.totalEventsEvicted}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Cache alder:</span>
                  <span className="font-semibold">
                    {stats.cacheAgeSeconds
                      ? `${Math.floor(stats.cacheAgeSeconds / 60)}m ${Math.floor(stats.cacheAgeSeconds % 60)}s`
                      : 'N/A'}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Restored fra storage:</span>
                  <span className="font-semibold">{stats.restoredFromStorageCount}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Sist lagret:</span>
                  <span className="font-semibold text-xs">
                    {stats.lastPersistedAt
                      ? new Date(stats.lastPersistedAt).toLocaleTimeString('nb-NO')
                      : 'Aldri'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* localStorage Statistics */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <HardDrive className="w-5 h-5" />
              localStorage statistikk
            </h3>
            
            {storageStats ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Brukt:</span>
                  <span className="font-semibold">
                    {localStorageHelper.formatBytes(storageStats.used)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Tilgjengelig:</span>
                  <span className="font-semibold">
                    {localStorageHelper.formatBytes(storageStats.available)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Bruk:</span>
                  <span className="font-semibold">{storageStats.percentage.toFixed(1)}%</span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      storageStats.percentage > 80 ? 'bg-red-600' : 'bg-green-600'
                    }`}
                    style={{ width: `${Math.min(storageStats.percentage, 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">localStorage ikke tilgjengelig</div>
            )}
          </div>
        </div>

        {/* Column 2: Query Interface */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {/* Query Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Search className="w-5 h-5" />
              Query API
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600 w-24">Query type:</label>
                <select
                  value={queryType}
                  onChange={(e) => setQueryType(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="location">Lokasjon</option>
                  <option value="rack">Reol</option>
                  <option value="timeWindow">Tidsvindu</option>
                </select>
              </div>

              {queryType === 'location' && (
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-600 w-24">Lokasjon:</label>
                  <input
                    type="text"
                    value={queryLocation}
                    onChange={(e) => setQueryLocation(e.target.value)}
                    placeholder="R01-V01-H00"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              )}

              {queryType === 'rack' && (
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-600 w-24">Reol:</label>
                  <input
                    type="number"
                    value={queryRack}
                    onChange={(e) => setQueryRack(Number(e.target.value))}
                    min="1"
                    max="99"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600 w-24">Tidsfilter:</label>
                <select
                  value={queryTimeWindow}
                  onChange={(e) => setQueryTimeWindow(e.target.value as TimeWindow)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value={TimeWindow.Last1Hour}>Siste time</option>
                  <option value={TimeWindow.Last4Hours}>Siste 4 timer</option>
                  <option value={TimeWindow.Last8Hours}>Siste 8 timer</option>
                  <option value={TimeWindow.Today}>I dag</option>
                </select>
              </div>

              <button
                onClick={executeQuery}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Search className="w-4 h-4" />
                Kjør query
              </button>
            </div>
          </div>

          {/* Query Results */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-1 overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Query resultater
            </h3>
            
            {queryResult ? (
              <div className="flex-1 overflow-y-auto">
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">
                      <CheckCircle className="w-4 h-4 inline mr-1 text-green-600" />
                      Fant <strong>{queryResult.totalCount}</strong> events
                    </span>
                    <span className="text-gray-500">
                      <Clock className="w-4 h-4 inline mr-1" />
                      {queryResult.executionTimeMs.toFixed(2)}ms
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {queryResult.events.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      Ingen events matchet query
                    </div>
                  ) : (
                    queryResult.events.map((event, index) => (
                      <div
                        key={`${event.binCode}-${event.timestamp}-${index}`}
                        className="p-3 rounded-lg border border-gray-200 bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono font-semibold text-sm">
                            {event.binCode}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                            {getActivityTypeName(event.activityType)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div>Vare: {event.itemNo} • Antall: {event.quantity} stk</div>
                          <div>Operatør: {event.operatorId} • Reol: {event.rackNumber}</div>
                          <div className="text-gray-500">
                            {new Date(event.timestamp).toLocaleString('nb-NO')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Info className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Ingen query kjørt ennå</p>
                  <p className="text-sm">Velg filter og klikk "Kjør query"</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Column 3: Event Log */}
        <div className="w-96 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Siste events
          </h3>
          
          <div className="flex-1 overflow-y-auto space-y-2">
            {eventLog.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                Ingen events ennå
              </div>
            ) : (
              eventLog.map((event, index) => (
                <div
                  key={`${event.binCode}-${event.timestamp}-${index}`}
                  className="p-3 rounded-lg border border-gray-200 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-semibold text-sm">
                      {event.binCode}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                      {getActivityTypeName(event.activityType)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    <div>Antall: {event.quantity} stk</div>
                    <div className="text-gray-500">
                      {new Date(event.timestamp).toLocaleTimeString('nb-NO')}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
        <p className="text-sm text-blue-800">
          <strong>Tips:</strong> Test cache-systemet ved å legge til events og se at de lagres til localStorage. 
          Refresh siden for å se at events gjenopprettes. Prøv queries for å filtrere på lokasjon, reol eller tidsvindu.
          Cache tømmes automatisk ved midnatt.
        </p>
      </div>
    </div>
  );
};

export default Fase5Test;
